import {
  DatabaseOutlined,
  FilterOutlined,
  SearchOutlined,
  TagOutlined,
} from "@ant-design/icons";
import { Button, Input, Popover, Select, Table } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { CREATE_jwel } from "../../../Config/Config";
import ServerPagination from "../../Utilities/Pagination";
import PdfExcelPrint from "../../Utilities/PdfExcelPrint";
import styles from "../shared/Inventory.module.css";
import InventoryPageHeader from "../shared/InventoryPageHeader";
import InventoryTable from "../shared/InventoryTable";
import StockFilterPopoverContent from "./StockFilterPopoverContent";

const { Option } = Select;

const OPTION_DOT_COLORS = [
  "#f59e0b",
  "#22c55e",
  "#3b82f6",
  "#ec4899",
  "#a855f7",
  "#14b8a6",
];
const getDotColor = (index) =>
  OPTION_DOT_COLORS[index % OPTION_DOT_COLORS.length];

const COLUMN_CONFIG = {
  sNo: { key: "serialNo", label: "S.No", disabled: true },
  designCode: { key: "DesignCode", label: "Design Code", disabled: true },
  brandCode: { key: "ItemCode", label: "Brand Code", disabled: true },
  brandName: { key: "CompName", label: "Brand Name", disabled: true },
  groupName: { key: "GroupName", label: "Group Name", disabled: true },
  productName: { key: "ProdName", label: "Product Name", disabled: true },
  prodSize: { key: "Prodsize", label: "Size", disabled: true },
  purchQty: { key: "PQTY", label: "Purchase Qty", total: true, disabled: true },
  saleQty: { key: "SaleQTY", label: "Sale Qty", disabled: true },
  qty: { key: "Qty", label: "Qty", total: true, disabled: true },
  dPrice: { key: "DPrice", label: "D Price", disabled: true },
  dlrAmt: {
    key: "DLRAMT",
    label: "Dealer Amount",
    total: true,
    disabled: true,
  },
  mrp: { key: "CPrice", label: "MRP", disabled: true },
  disPer: { key: "Disper", label: "Dis %", disabled: true },
  disAmt: { key: "Disamt", label: "Dis Amt", disabled: true },
  offer: { key: "offer", label: "OFFER", disabled: true },
  saleValue: {
    key: "SaleValue",
    label: "Sale Value",
    total: true,
    disabled: true,
  },
  invNo: { key: "InvNO", label: "Inv No" },
  invDate: { key: "Invdate", label: "Inv Data" },
  city: { key: "Area", label: "City" },
  agentName: { key: "AGENTNAME", label: "Agent" },
  color: { key: "COLOUR", label: "Color" },
  fashion: { key: "SLEVES", label: "Fashion" },
  hsnCode: { key: "HSNCODE", label: "HSN" },
  gst: { key: "GST", label: "GST" },
  partyName: { key: "Partyname", label: "Party Name" },
  branchName: { key: "BRANCHNAME", label: "Branch Name" },
  entryNo: { key: "ENTRYNO", label: "Entry No" },
  entryDate: { key: "ENTRYDATE", label: "Entry Date" },
};

const initialCheckboxState = Object.keys(COLUMN_CONFIG).reduce((acc, key) => {
  acc[key] = true;
  return acc;
}, {});

// Column checkbox list for the filter popup
const columnOptions = Object.entries(COLUMN_CONFIG).map(([value, cfg]) => ({
  value,
  label: cfg.label,
  disabled: cfg.disabled === true,
}));

const initialFilters = {
  productSize: null,
  color: null,
  offer: null,
  fashion: null,
  productName: null,
  brandName: null,
  groupName: null,
  city: null,
  agentName: null,
  invNo: null,
  entryNo: null,
  branchName: null,
  hsnCode: null,
  gst: null,
  fromPrice: 0,
  toPrice: 0,
};

/* ---------------- Shared card-view field maps (mobile) ---------------- */
const primaryFields = [
  { key: "DesignCode", label: "Design Code" },
  { key: "ProdName", label: "Product", badge: "productname" },
  { key: "Qty", label: "Qty", badge: "pieces" },
  { key: "DLRAMT", label: "Dlr Amt", badge: "netwt" },
];
const secondaryFields = [
  { key: "CompName", label: "Brand" },
  { key: "InvNO", label: "Inv No" },
  { key: "Invdate", label: "Inv Date" },
  { key: "SaleValue", label: "Sale Value" },
];
const DEFAULT_PAGE_SIZE = 20;

const StockPoint = () => {
  const [selectCheckBox, setSelectCheckBox] = useState(initialCheckboxState);
  const [tempSelectCheckBox, setTempSelectCheckBox] =
    useState(initialCheckboxState);

  const [filters, setFilters] = useState(initialFilters);
  const [tempFilters, setTempFilters] = useState(initialFilters);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [tableVisible, setTableVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ptypeLoading, setPtypeLoading] = useState(false);
  const [puschase, setPurchase] = useState();
  const [selectedPType, setSelectedPType] = useState();
  const [disBarCode, setDisBarCode] = useState();
  const tenantName = localStorage.getItem("tenantName");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalRecords, setTotalRecords] = useState(0);

  /* ── purchase type list ── */
  useEffect(() => {
    setPtypeLoading(true);
    axios
      .get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithOrder?tableName=PURCHASE_TYPE&order=PTYPE`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      )
      .then((response) => {
        const detailsData = response.data;
        setPurchase(detailsData);
      })
      .catch((error) => {
        console.error("Error fetching purchase types:", error);
      })
      .finally(() => setPtypeLoading(false));
  }, []);

  /* ── fetch stock ── */
  useEffect(() => {
    if (!selectedPType) {
      setData([]);
      setTotalRecords(0);
      return;
    }
    const controller = new AbortController();
    setTableVisible(false);

    const fetchStockData = async () => {
      try {
        setLoading(true);

        const response = await axios.get(`${CREATE_jwel}/api/Filter/GetData`, {
          params: {
            tableName: "current_stock",
            field: "PTYPE",
            value: selectedPType,
            pageNumber,
            pageSize,
          },
          headers: {
            tenantName: tenantName,
          },
          signal: controller.signal,
        });

        const res = response.data || {};
        const rows = Array.isArray(res.data) ? res.data : [];
        const offset = (pageNumber - 1) * pageSize;

        const calculatedData = rows
          // do not show items whose Qty is 0
          .filter((item) => Number(item?.Qty || 0) > 0)
          .map((item, index) => ({
            ...item,
            key: offset + index + 1,
            serialNo: offset + index + 1,
            SaleQTY: (item?.Qty || 0) * (item?.PQTY || 0),
            DLRAMT: (item?.Qty || 0) * (item?.DPrice || 0),
            SaleValue: (item?.Qty || 0) * (item?.offer || 0),
          }));

        setData(calculatedData);
        setTotalRecords(Number(res.totalRecords) || 0);
        setTimeout(() => setTableVisible(true), 80);
      } catch (error) {
        if (axios.isCancel?.(error) || error?.name === "CanceledError") return;
        console.error("Error fetching current stock data:", error);
        setData([]);
        setTotalRecords(0);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchStockData();
    return () => controller.abort();
  }, [selectedPType, pageNumber, pageSize]);

  /* ── client-side filters ── */
  useEffect(() => {
    let filtered = [...data];

    // design / brand code search (current page)
    if (disBarCode && disBarCode.trim()) {
      const q = disBarCode.trim().toLowerCase();
      filtered = filtered.filter(
        (item) =>
          String(item.DesignCode || "")
            .toLowerCase()
            .includes(q) ||
          String(item.ItemCode || "")
            .toLowerCase()
            .includes(q),
      );
    }

    if (filters.productSize) {
      filtered = filtered.filter(
        (item) => item.Prodsize === filters.productSize,
      );
    }

    if (filters.color) {
      filtered = filtered.filter((item) => item.COLOUR === filters.color);
    }

    if (filters.offer) {
      filtered = filtered.filter((item) => item.offer === filters.offer);
    }

    if (filters.fashion) {
      filtered = filtered.filter((item) => item.SLEVES === filters.fashion);
    }

    if (filters.productName) {
      filtered = filtered.filter(
        (item) => item.ProdName === filters.productName,
      );
    }

    if (filters.brandName) {
      filtered = filtered.filter((item) => item.CompName === filters.brandName);
    }

    if (filters.groupName) {
      filtered = filtered.filter(
        (item) => item.GroupName === filters.groupName,
      );
    }

    if (filters.city) {
      filtered = filtered.filter((item) => item.Area === filters.city);
    }

    if (filters.agentName) {
      filtered = filtered.filter(
        (item) => item.AGENTNAME === filters.agentName,
      );
    }

    if (filters.invNo) {
      filtered = filtered.filter((item) => item.InvNO === filters.invNo);
    }

    if (filters.entryNo) {
      filtered = filtered.filter((item) => item.ENTRYNO === filters.entryNo);
    }

    if (filters.branchName) {
      filtered = filtered.filter(
        (item) => item.BRANCHNAME === filters.branchName,
      );
    }

    if (filters.hsnCode) {
      filtered = filtered.filter((item) => item.HSNCODE === filters.hsnCode);
    }

    if (filters.gst !== null && filters.gst !== undefined) {
      filtered = filtered.filter((item) => item.GST === filters.gst);
    }

    const fromPrice = Number(filters.fromPrice) || 0;
    const toPrice = Number(filters.toPrice) || 0;
    if (fromPrice || toPrice) {
      filtered = filtered.filter((item) => {
        const price = item.CPrice || 0;
        return (
          (!fromPrice || price >= fromPrice) && (!toPrice || price <= toPrice)
        );
      });
    }

    setFilteredData(
      filtered.map((item, index) => ({
        ...item,
        sno: index + 1,
      })),
    );
  }, [filters, data, disBarCode]);

  const applyFilters = () => {
    setFilters(tempFilters);
    setSelectCheckBox(tempSelectCheckBox);
    setPopoverVisible(false);
  };

  const clearFilters = () => {
    setTempFilters(initialFilters);
    setFilters(initialFilters);
    setSelectCheckBox(initialCheckboxState);
    setTempSelectCheckBox(initialCheckboxState);
    setPopoverVisible(false);
  };

  /* ── pagination handlers (NEW) ── */
  const handlePageChange = (page, size) => {
    // changing page size should go back to page 1
    if (size !== pageSize) {
      setPageSize(size);
      setPageNumber(1);
    } else {
      setPageNumber(page);
    }
  };

  const handlePTypeChange = (value) => {
    setSelectedPType(value ?? undefined);
    setPageNumber(1);
  };

  const columns = [
    {
      title: "S.No",
      dataIndex: "serialNo",
      width: 50,
      align: "center",
      key: "serialNo",
      className: "col-sno",
    },
    {
      title: "Design Code",
      dataIndex: "DesignCode",
      key: "DesignCode",
      align: "left",
      className: "col-tagno",
      render: (value) => <span className="tagBadge">{value}</span>,
    },
    {
      title: "Brand Code",
      dataIndex: "ItemCode",
      key: "ItemCode",
      align: "left",
    },
    {
      title: "Brand Name",
      dataIndex: "CompName",
      key: "CompName",
      align: "left",
    },
    {
      title: "Group Name",
      dataIndex: "GroupName",
      key: "GroupName",
      align: "left",
    },
    {
      title: "Product Name",
      dataIndex: "ProdName",
      key: "ProdName",
      align: "left",
      className: "col-productname",
    },
    {
      title: "Prod Size",
      dataIndex: "Prodsize",
      key: "Prodsize",
      align: "left",
    },
    {
      title: "Purch Qty",
      dataIndex: "PQTY",
      key: "PQTY",
      align: "right",
    },
    {
      title: "Sale Qty",
      dataIndex: "SaleQTY",
      key: "SaleQTY",
      align: "right",
      render: (value, record) => <span>{record?.Qty * record?.PQTY}</span>,
    },
    {
      title: "Qty",
      dataIndex: "Qty",
      key: "Qty",
      align: "center",
      className: "col-pieces",
      render: (value) => <span className="piecesBadge">{value}</span>,
    },
    {
      title: "Dlr.Price",
      dataIndex: "DPrice",
      key: "DPrice",
      align: "right",
    },
    {
      title: "Dlr.Amt",
      dataIndex: "DLRAMT",
      key: "DLRAMT",
      align: "right",
      className: "col-netwt",
      render: (value, record) => (
        <span className="wtBadge">
          {(record?.Qty * record?.DPrice)?.toFixed(2)}
        </span>
      ),
    },
    {
      title: "MRP",
      dataIndex: "CPrice",
      key: "CPrice",
      align: "right",
      render: (value) => <span>{value?.toFixed(2)}</span>,
    },
    {
      title: "Dis %",
      dataIndex: "Disper",
      key: "Disper",
      align: "right",
    },
    {
      title: "Dis.Amt",
      dataIndex: "Disamt",
      key: "Disamt",
      align: "right",
    },
    {
      title: "Offer",
      dataIndex: "offer",
      key: "offer",
      align: "right",
    },
    {
      title: "Sale Value",
      dataIndex: "SaleValue",
      key: "SaleValue",
      align: "right",
      render: (value, record) => (
        <span>{(record?.Qty * record?.offer)?.toFixed(2)}</span>
      ),
    },
    {
      title: "Inv No",
      dataIndex: "InvNO",
      key: "InvNO",
      align: "left",
    },
    {
      title: "Inv Date",
      dataIndex: "Invdate",
      key: "Invdate",
      align: "center",
      className: "col-date",
    },
    {
      title: "City",
      dataIndex: "Area",
      key: "Area",
      align: "left",
    },
    {
      title: "Agent Name",
      dataIndex: "AGENTNAME",
      key: "AGENTNAME",
      align: "left",
      className: "col-dealername",
    },
    {
      title: "Color",
      dataIndex: "COLOUR",
      key: "COLOUR",
      align: "left",
    },
    {
      title: "Fashion",
      dataIndex: "SLEVES",
      key: "SLEVES",
      align: "left",
    },
    {
      title: "HSN Code",
      dataIndex: "HSNCODE",
      key: "HSNCODE",
      align: "left",
    },
    {
      title: "Gst",
      dataIndex: "GST",
      key: "GST",
      align: "right",
      className: "col-diaamt",
    },
    {
      title: "Supplier Name",
      dataIndex: "Partyname",
      key: "Partyname",
      align: "left",
    },
    {
      title: "Branch Name",
      dataIndex: "BRANCHNAME",
      key: "BRANCHNAME",
      align: "left",
    },
    {
      title: "Entry No",
      dataIndex: "ENTRYNO",
      key: "ENTRYNO",
      align: "left",
    },
    {
      title: "Entry Date",
      dataIndex: "ENTRYDATE",
      key: "ENTRYDATE",
      align: "center",
    },
  ];

  const getTotals = (data) => {
    return data.reduce(
      (acc, item) => {
        acc.Qty += Number(item.Qty || 0);
        acc.DLRAMT += Number(item.DLRAMT || 0);
        acc.SaleValue += Number(item.SaleValue || 0);
        return acc;
      },
      {
        Qty: 0,
        DLRAMT: 0,
        SaleValue: 0,
      },
    );
  };

  const formattedData = useMemo(
    () =>
      filteredData.map((item) => ({
        ...item,
        DPrice: Number(item.DPrice || 0),
        DLRAMT: Number(item.DLRAMT || 0),
        Invdate: item?.Invdate ? dayjs(item.Invdate).format("DD/MMM/YYYY") : "",
        ENTRYDATE: item?.ENTRYDATE
          ? dayjs(item.ENTRYDATE).format("DD/MMM/YYYY")
          : "",
      })),
    [filteredData],
  );

  const getUniqueValues = (data = [], key) => {
    return [...new Set(data.map((item) => item[key]).filter(Boolean))];
  };
  const totals = useMemo(() => getTotals(filteredData), [filteredData]);

  const filteredColumns = columns.filter((col) =>
    Object.entries(COLUMN_CONFIG).some(
      ([checkKey, cfg]) => cfg.key === col.key && selectCheckBox[checkKey],
    ),
  );

  /* dropdown options for the filter popup */
  const uniqueOptions = {
    productSizes: getUniqueValues(data, "Prodsize"),
    colors: getUniqueValues(data, "COLOUR"),
    offers: getUniqueValues(data, "offer"),
    fashions: getUniqueValues(data, "SLEVES"),
    productNames: getUniqueValues(data, "ProdName"),
    brandNames: getUniqueValues(data, "CompName"),
    groupNames: getUniqueValues(data, "GroupName"),
    cities: getUniqueValues(data, "Area"),
    agentNames: getUniqueValues(data, "AGENTNAME"),
    entryNos: getUniqueValues(data, "ENTRYNO"),
    invNos: getUniqueValues(data, "InvNO"),
    branchNames: getUniqueValues(data, "BRANCHNAME"),
    hsnCodes: getUniqueValues(data, "HSNCODE"),
    gsts: getUniqueValues(data, "GST"),
  };

  const filterContent = (
    <StockFilterPopoverContent
      tempFilters={tempFilters}
      setTempFilters={setTempFilters}
      uniqueOptions={uniqueOptions}
      onApply={applyFilters}
      onClear={clearFilters}
      tempSelectCheckBox={tempSelectCheckBox}
      setTempSelectCheckBox={setTempSelectCheckBox}
      columnOptions={columnOptions}
    />
  );

  /* ---------------- Export data for PdfExcelPrint ---------------- */
  const exportData = [
    ...formattedData,
    {
      serialNo: "Total",
      DesignCode: "",
      ItemCode: "",
      CompName: "",
      GroupName: "",
      ProdName: "",
      Prodsize: "",
      PQTY: "",
      SaleQTY: "",
      Qty: totals.Qty,
      DPrice: "",
      DLRAMT: totals.DLRAMT.toFixed(2),
      CPrice: "",
      Disper: "",
      Disamt: "",
      offer: "",
      SaleValue: totals.SaleValue.toFixed(2),
    },
  ];

  return (
    <div className={styles.page}>
      <InventoryPageHeader
        title="Stock Point"
        subtitle="Inventory · Stock"
        icon={<DatabaseOutlined style={{ color: "#fff", fontSize: 16 }} />}
        recordCount={totalRecords}
        visible={totalRecords > 0}
        stats={[
          { label: "Records", value: totalRecords, color: "blue" },
          { label: "Qty", value: totals.Qty, color: "orange" },
          {
            label: "Dlr Amt",
            value: totals.DLRAMT.toFixed(2),
            color: "amber",
          },
          {
            label: "Sale Value",
            value: totals.SaleValue.toFixed(2),
            color: "green",
          },
        ]}
      />

      <div className={styles.toolbar} style={{ flexWrap: "wrap", rowGap: 12 }}>
        <div
          className={styles.toolbarLeft}
          style={{ flexWrap: "wrap", rowGap: 12 }}
        >
          <div className="mainProductHighlight">
            <span className="mainProductIcon">
              <TagOutlined />
            </span>
            <div className="mainProductInner">
              <div className={styles.toolbarLabel}>Purchase Type</div>
              <Select
                showSearch
                allowClear
                placeholder="Select Purchase Type"
                loading={ptypeLoading}
                className="mainProductPicker"
                popupClassName="mainProductDropdown"
                style={{ width: "12rem" }}
                value={selectedPType}
                onChange={handlePTypeChange}
                filterOption={(input, option) =>
                  (option?.searchText || "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                suffixIcon={<SearchOutlined style={{ color: "#1677ff" }} />}
              >
                {(puschase || []).map((item, index) => {
                  const dotColor = getDotColor(index);
                  return (
                    <Option
                      key={item?.PType}
                      value={item?.PType}
                      searchText={`${item?.PType}`}
                    >
                      <span
                        className="option-dot"
                        style={{
                          background: dotColor,
                          "--dot-glow": `${dotColor}66`,
                        }}
                      />
                      {item?.PType}
                    </Option>
                  );
                })}
              </Select>
            </div>
          </div>

          <div className="mainProductHighlight">
            <span className="mainProductIcon">
              <SearchOutlined />
            </span>
            <div className="mainProductInner">
              <div className={styles.toolbarLabel}>Design cod/Brand Code</div>
              <Input
                placeholder="Design cod/Brand Code"
                className="mainProductPicker"
                style={{ width: "100%" }}
                value={disBarCode}
                onChange={(e) => setDisBarCode(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className={styles.toolbarRight}>
          <PdfExcelPrint
            data={exportData}
            columns={filteredColumns}
            fileName="StockPoint"
            disabled={formattedData.length === 0}
          />
          <Popover
            content={filterContent}
            trigger="click"
            open={popoverVisible}
            onOpenChange={setPopoverVisible}
            placement="bottomRight"
            overlayInnerStyle={{ padding: 0, borderRadius: 8 }}
          >
            <Button icon={<FilterOutlined />} type="primary">
              Filter
            </Button>
          </Popover>
        </div>
      </div>

      <InventoryTable
        columns={filteredColumns}
        dataSource={formattedData}
        rowKey="serialNo"
        tableClassName="mr-table"
        tableEnter={tableVisible}
        loading={loading}
        emptyText="No stock records found for the selected filters"
        primaryFields={primaryFields}
        secondaryFields={secondaryFields}
        pagination={false}
        mobilePageSize={pageSize}
        summary={() => (
          <Table.Summary.Row style={{ background: "#f5f5f5", fontWeight: 600 }}>
            {filteredColumns.map((col, index) => {
              if (index === 0) {
                return (
                  <Table.Summary.Cell key={col.key}>Total</Table.Summary.Cell>
                );
              }

              if (col.key === "Qty") {
                return (
                  <Table.Summary.Cell key={col.key} align="right">
                    {totals.Qty}
                  </Table.Summary.Cell>
                );
              }

              if (col.key === "DLRAMT") {
                return (
                  <Table.Summary.Cell key={col.key} align="right">
                    {totals.DLRAMT.toFixed(2)}
                  </Table.Summary.Cell>
                );
              }

              if (col.key === "SaleValue") {
                return (
                  <Table.Summary.Cell key={col.key} align="right">
                    {totals.SaleValue.toFixed(2)}
                  </Table.Summary.Cell>
                );
              }

              return <Table.Summary.Cell key={col.key} />;
            })}
          </Table.Summary.Row>
        )}
      />
      <ServerPagination
        current={pageNumber}
        pageSize={pageSize}
        total={totalRecords}
        loading={loading}
        onChange={handlePageChange}
      />

      <style>{`
  @keyframes mainProductPulse {
    0%   { box-shadow: 0 0 0 0 rgba(24,144,255,0.30); }
    70%  { box-shadow: 0 0 0 8px rgba(24,144,255,0); }
    100% { box-shadow: 0 0 0 0 rgba(24,144,255,0); }
  }
  .mainProductHighlight {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 18px 8px 10px;
    border-radius: 14px;
    background: #eef6ff;
    border: 1.5px solid #65aefd;
    box-shadow: 0 0 0 1.5px #bcdcff inset;
    animation: mainProductPulse 2.4s infinite;
    min-width: 210px;
    flex-shrink: 0;
  }
  .mainProductIcon {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(24,144,255,0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #1677ff;
    flex-shrink: 0;
  }
  .mainProductInner {
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex: 1;
    min-width: 0;
  }
  .mainProductPicker.ant-picker,
  .mainProductPicker.ant-select .ant-select-selector,
  .mainProductPicker.ant-input {
    height: 34px !important;
    border-radius: 8px !important;
    background: #ffffff !important;
    border: 1px solid #d6e8ff !important;
    width: 100%;
  }
  .mainProductPicker.ant-select:hover .ant-select-selector {
    border-color: #65aefd !important;
  }
  .mainProductPicker.ant-select.ant-select-focused .ant-select-selector,
  .mainProductPicker.ant-input:focus {
    border-color: #1890ff !important;
    box-shadow: none !important;
  }
  .mainProductPicker.ant-select .ant-select-selector {
    display: flex;
    align-items: center;
    padding: 0 11px !important;
  }
  .mainProductPicker.ant-select .ant-select-selection-search-input {
    height: 32px !important;
  }
  .mainProductPicker.ant-select .ant-select-selection-placeholder,
  .mainProductPicker.ant-select .ant-select-selection-item {
    line-height: 32px !important;
    font-size: 13px;
    font-weight: 600;
    color: #1f2430;
  }
  .mainProductPicker.ant-select .ant-select-selection-placeholder {
    font-weight: 400;
    color: #a3a9b5;
  }
  .mainProductPicker.ant-select .ant-select-arrow {
    color: #1677ff;
    right: 11px;
  }

  /* ── Purchase Type dropdown list ── */
  @keyframes optionDotPulse {
    0%   { box-shadow: 0 0 0 0 var(--dot-glow); }
    70%  { box-shadow: 0 0 0 5px transparent; }
    100% { box-shadow: 0 0 0 0 transparent; }
  }
  .mainProductDropdown .ant-select-item {
    font-weight: 600;
    font-size: 13.5px;
    padding: 9px 12px;
    color: #1f2430;
  }
  .mainProductDropdown .ant-select-item-option-active {
    background: #f0f7ff !important;
  }
  .mainProductDropdown .ant-select-item-option-selected {
    background: #e6f4ff !important;
    font-weight: 700;
  }
  .mainProductDropdown .option-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 10px;
    animation: optionDotPulse 1.8s infinite;
    vertical-align: middle;
  }

  .ant-table-thead > tr > th {
    white-space: nowrap !important;
  }
`}</style>
    </div>
  );
};

export default StockPoint;
